import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  acceptJobSignup,
  createEventVolunteerJob,
  followOrganizerForVolunteerNotices,
  listEventVolunteerJobs,
  listJobSignups,
  signUpForShift,
  withdrawFromShift,
} from "@/api/admits-seva-album";

/**
 * Seva on any host's event: jobs with shifts and a minimum age. Everyone sees
 * how full each shift is; the host also sees who signed up and accepts them.
 */
export function SevaPanel({ eventId }: { eventId: string }) {
  const qc = useQueryClient();
  const [title, setTitle] = useState("Kitchen");
  const [minimumAge, setMinimumAge] = useState(13);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [band, setBand] = useState("");
  const [organizerId, setOrganizerId] = useState("");
  const [openJob, setOpenJob] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const jobs = useQuery({ queryKey: ["seva", eventId], queryFn: () => listEventVolunteerJobs(eventId) });
  const signups = useQuery({
    queryKey: ["seva-signups", openJob],
    queryFn: () => listJobSignups(openJob!),
    enabled: Boolean(openJob),
  });

  async function run(fn: () => Promise<unknown>, done: string) {
    setMsg(null);
    try {
      await fn();
      setMsg(done);
      await qc.invalidateQueries({ queryKey: ["seva", eventId] });
      await qc.invalidateQueries({ queryKey: ["seva-signups"] });
    } catch (e) {
      setMsg(e instanceof Error ? e.message : "That did not work");
    }
  }

  const addJob = () =>
    run(
      () =>
        createEventVolunteerJob(eventId, {
          title,
          description: `${title} on the day`,
          category: title.toLowerCase().slice(0, 50),
          minimumAge,
          shifts: [
            { date, startTime: "14:00", endTime: "18:00", spotsAvailable: 8 },
            { date, startTime: "18:00", endTime: "22:00", spotsAvailable: 8 },
          ],
        }),
      "Job added.",
    );

  return (
    <div className="space-y-2">
      <h3 className="text-sm font-medium text-gray-700">Helpers (seva)</h3>
      <div className="flex gap-2 flex-wrap items-center text-sm">
        <input className="input w-32" value={title} onChange={(e) => setTitle(e.target.value)} />
        <input className="input w-36" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        <label className="text-xs text-gray-600">
          min age <input className="input w-16" type="number" min={0} value={minimumAge} onChange={(e) => setMinimumAge(Number(e.target.value))} />
        </label>
        <button className="px-3 py-1 rounded border border-gray-300" onClick={addJob}>
          Add job, two shifts
        </button>
      </div>
      <label className="text-xs text-gray-600 block">
        A child coming along, who takes a spot of their own{" "}
        <select className="input w-28" value={band} onChange={(e) => setBand(e.target.value)}>
          <option value="">no child</option>
          <option value="5-8">aged 5-8</option>
          <option value="9-12">aged 9-12</option>
          <option value="13-17">aged 13-17</option>
        </select>
      </label>
      <ul className="text-sm text-gray-600 space-y-2">
        {(jobs.data?.data.items ?? []).map((job) => (
          <li key={job.id} className="border border-gray-200 rounded p-2">
            <p>
              {job.title} · {job.spotsFilled} of {job.spotsAvailable}
              {job.minimumAge !== null ? ` · ${job.minimumAge} and over` : ""}{" "}
              <button className="text-xs underline" onClick={() => setOpenJob(openJob === job.id ? null : job.id)}>
                {openJob === job.id ? "hide names" : "names (host)"}
              </button>
            </p>
            {job.shifts.map((shift) => (
              <p key={shift.id} className="text-xs flex gap-2">
                {shift.date.slice(0, 10)} {shift.startTime}–{shift.endTime} · {shift.spotsFilled} of {shift.spotsAvailable}
                <button className="underline" onClick={() => run(() => signUpForShift(job.id, shift.id, band ? [band] : []), "Signed up.")}>
                  sign up
                </button>
                <button className="underline" onClick={() => run(() => withdrawFromShift(job.id, shift.id), "Withdrawn.")}>
                  withdraw
                </button>
              </p>
            ))}
            {openJob === job.id &&
              (signups.data?.data.signups ?? []).map((s) => (
                <p key={s.id} className="text-xs">
                  {s.user.name} · {s.status}
                  {s.childAgeBands.length ? ` · with ${s.childAgeBands.join(", ")}` : ""}{" "}
                  {s.status === "applied" && (
                    <button className="underline" onClick={() => run(() => acceptJobSignup(job.id, s.id), "Accepted.")}>
                      accept
                    </button>
                  )}
                </p>
              ))}
          </li>
        ))}
      </ul>
      <div className="flex gap-2 items-center text-xs">
        <input className="input w-72" placeholder="Organiser page id" value={organizerId} onChange={(e) => setOrganizerId(e.target.value)} />
        <button className="underline" disabled={!organizerId} onClick={() => run(() => followOrganizerForVolunteerNotices(organizerId), "Following, with volunteer notices.")}>
          follow for volunteer notices
        </button>
      </div>
      {msg && <p className="text-xs text-gray-600">{msg}</p>}
    </div>
  );
}
